/**
 * 距離・時間データのキャッシュサービス
 *
 * 特徴:
 * - 日次限定（翌日に自動無効化）
 * - LocalStorage ベース（最大100エントリー）
 * - LRU（Least Recently Used）削除で容量管理
 * - ブラウザ互換性フォールバック
 *
 * キャッシュキー: distance_YYYY-MM-DD_正規化済from_正規化済to
 */

export interface CachedDistanceData {
  distance: number; // メートル
  duration: number; // 秒
  cachedAt: string; // ISO 8601形式
  fromAddress: string; // 元の住所
  toAddress: string; // 元の住所
  accessedAt: string; // 最後にアクセスされた時刻（LRU用）
}

export class DistanceCache {
  private readonly CACHE_PREFIX = 'rakuhai_distance_cache_';
  private readonly MAX_CACHE_SIZE = 100;

  /**
   * 住所を正規化
   * - 空白・ハイフン削除
   * - 小文字に統一
   * - 50文字で切り詰め
   */
  private normalizeAddress(address: string): string {
    return address.toLowerCase().replace(/[\s-]/g, '').substring(0, 50);
  }

  /**
   * キャッシュキーを生成
   */
  private getCacheKey(from: string, to: string): string {
    const today = new Date();
    const date = today.toISOString().split('T')[0]; // YYYY-MM-DD
    const normalizedFrom = this.normalizeAddress(from);
    const normalizedTo = this.normalizeAddress(to);
    return `${this.CACHE_PREFIX}${date}_${normalizedFrom}_${normalizedTo}`;
  }

  /**
   * キャッシュから取得
   * @returns キャッシュデータ、または存在しない/期限切れの場合null
   */
  public get(from: string, to: string): CachedDistanceData | null {
    try {
      const key = this.getCacheKey(from, to);
      const cached = localStorage.getItem(key);

      if (!cached) {
        return null;
      }

      const data: CachedDistanceData = JSON.parse(cached);

      // キャッシュ日付が今日のものかチェック
      const cacheDate = new Date(data.cachedAt).toISOString().split('T')[0];
      const today = new Date().toISOString().split('T')[0];

      if (cacheDate !== today) {
        // 期限切れ - 削除
        localStorage.removeItem(key);
        return null;
      }

      // LRU用にアクセス時刻を更新
      data.accessedAt = new Date().toISOString();
      localStorage.setItem(key, JSON.stringify(data));

      return data;
    } catch (error) {
      // LocalStorage アクセス失敗時はnullを返す
      console.warn('Distance cache get failed:', error);
      return null;
    }
  }

  /**
   * キャッシュに保存
   */
  public set(from: string, to: string, distance: number, duration: number): void {
    try {
      // 容量チェック - 必要に応じてLRU削除
      this.enforceSizeLimit();

      const key = this.getCacheKey(from, to);
      const now = new Date().toISOString();

      const data: CachedDistanceData = {
        distance,
        duration,
        cachedAt: now,
        fromAddress: from,
        toAddress: to,
        accessedAt: now,
      };

      localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      // LocalStorage 容量不足などの場合は無視
      console.warn('Distance cache set failed:', error);
    }
  }

  /**
   * キャッシュエントリーを削除
   */
  public delete(from: string, to: string): void {
    try {
      const key = this.getCacheKey(from, to);
      localStorage.removeItem(key);
    } catch (error) {
      console.warn('Distance cache delete failed:', error);
    }
  }

  /**
   * すべてのキャッシュをクリア
   */
  public clear(): void {
    try {
      const keysToDelete: string[] = [];

      // キャッシュキーをすべて取得
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.CACHE_PREFIX)) {
          keysToDelete.push(key);
        }
      }

      // 削除
      keysToDelete.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.warn('Distance cache clear failed:', error);
    }
  }

  /**
   * 日次クリーンアップ（起動時に呼び出す）
   * - 期限切れキャッシュを削除
   * - 容量制限を適用
   */
  public cleanup(): void {
    try {
      const keysToDelete: string[] = [];
      const today = new Date().toISOString().split('T')[0];

      // 期限切れキャッシュを検出
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);

        if (!key || !key.startsWith(this.CACHE_PREFIX)) {
          continue;
        }

        const cached = localStorage.getItem(key);
        if (!cached) {
          continue;
        }

        try {
          const data: CachedDistanceData = JSON.parse(cached);
          const cacheDate = new Date(data.cachedAt).toISOString().split('T')[0];

          if (cacheDate !== today) {
            keysToDelete.push(key);
          }
        } catch {
          // パース失敗したキャッシュは削除
          keysToDelete.push(key);
        }
      }

      // 削除実行
      keysToDelete.forEach(key => localStorage.removeItem(key));

      // 容量制限を適用
      this.enforceSizeLimit();
    } catch (error) {
      console.warn('Distance cache cleanup failed:', error);
    }
  }

  /**
   * 容量制限を適用（LRU削除）
   * 最大100エントリーを超える場合、最後にアクセスされたのが古いものから削除
   */
  private enforceSizeLimit(): void {
    try {
      const entries: Array<[string, CachedDistanceData | null]> = [];

      // すべてのキャッシュエントリーを収集
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);

        if (!key || !key.startsWith(this.CACHE_PREFIX)) {
          continue;
        }

        const cached = localStorage.getItem(key);
        if (!cached) {
          continue;
        }

        try {
          const data: CachedDistanceData = JSON.parse(cached);
          entries.push([key, data]);
        } catch {
          entries.push([key, null]);
        }
      }

      // 最大数を超える場合、古い順に削除
      if (entries.length >= this.MAX_CACHE_SIZE) {
        // accessedAtでソート（古い順）
        entries.sort((a, b) => {
          const aTime = a[1]?.accessedAt || a[1]?.cachedAt || '';
          const bTime = b[1]?.accessedAt || b[1]?.cachedAt || '';
          return new Date(aTime).getTime() - new Date(bTime).getTime();
        });

        // 最古のエントリーを削除
        const toDelete = entries.length - this.MAX_CACHE_SIZE + 1;
        for (let i = 0; i < toDelete && i < entries.length; i++) {
          localStorage.removeItem(entries[i][0]);
        }
      }
    } catch (error) {
      console.warn('Distance cache enforceSize limit failed:', error);
    }
  }

  /**
   * キャッシュ統計情報を取得（デバッグ用）
   */
  public getStats(): {
    count: number;
    size: number;
    oldestEntry?: string;
    newestEntry?: string;
  } {
    try {
      let count = 0;
      let size = 0;
      let oldestTime = Infinity;
      let newestTime = 0;
      let oldestKey = '';
      let newestKey = '';

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);

        if (!key || !key.startsWith(this.CACHE_PREFIX)) {
          continue;
        }

        const cached = localStorage.getItem(key);
        if (!cached) {
          continue;
        }

        count++;
        size += cached.length;

        try {
          const data: CachedDistanceData = JSON.parse(cached);
          const time = new Date(data.accessedAt || data.cachedAt).getTime();

          if (time < oldestTime) {
            oldestTime = time;
            oldestKey = key;
          }

          if (time > newestTime) {
            newestTime = time;
            newestKey = key;
          }
        } catch {
          // パース失敗時は無視
        }
      }

      return {
        count,
        size,
        oldestEntry: oldestKey || undefined,
        newestEntry: newestKey || undefined,
      };
    } catch (error) {
      console.warn('Distance cache getStats failed:', error);
      return { count: 0, size: 0 };
    }
  }
}

// シングルトンインスタンス
export const distanceCache = new DistanceCache();
